import type { JSX, ReactNode } from "react";
import { Drawer } from "vaul";

import { cn } from "@repo/cn";

import { Button } from "@/components/ui/button/button";

export interface DrawerProps {
  dismissible?: boolean;
  open: boolean;
  children: ReactNode;
  setOpen: (open: boolean) => void;
  onClose?: () => void;
}

export interface DrawerTriggerProps {
  children: ReactNode;
  asChild?: boolean;
}

export interface DrawerHeaderProps {
  children: ReactNode;
}

export interface DrawerTitleProps {
  children: ReactNode;
  className?: string;
}

export interface DrawerDescriptionProps {
  children: ReactNode;
}

export interface DrawerFooterProps {
  children: ReactNode;
}

export interface DrawerContentProps {
  children: ReactNode;
  className?: string;
  dismissible?: boolean;
  noPadding?: boolean;
}

export interface DrawerCloseProps {
  children: string;
  onClose: () => void;
}

interface DrawerBodyProps {
  children: ReactNode;
}

export const DrawerTrigger = ({ children, asChild }: DrawerTriggerProps): JSX.Element => {
  return <Drawer.Trigger asChild={asChild}>{children}</Drawer.Trigger>;
};

export const DrawerRoot = ({
  dismissible = true,
  open,
  setOpen,
  onClose,
  children,
}: DrawerProps): JSX.Element => {
  const handleOpenChange = (isOpen: boolean): void => {
    setOpen(isOpen);
  };

  return (
    <Drawer.Root
      dismissible={dismissible}
      open={open}
      onOpenChange={handleOpenChange}
      onClose={onClose}
    >
      {children}
    </Drawer.Root>
  );
};

export const DrawerHeader = ({ children }: DrawerHeaderProps): JSX.Element => {
  return <div className="shrink-0 mb-2">{children}</div>;
};

export const DrawerFooter = ({ children }: DrawerFooterProps): JSX.Element => {
  return (
    <div className="shrink-0 flex justify-center flex-col items-center gap-4">
      {children}
    </div>
  );
};

export const DrawerBody = ({ children }: DrawerBodyProps): JSX.Element => {
  return (
    <div className="flex-1 overflow-auto scrollbar-hidden overscroll-none text-neutral-500 font-normal min-h-0 max-h-[55vh]">
      {children}
    </div>
  );
};

export const DrawerTitle = ({ children, className }: DrawerTitleProps): JSX.Element => {
  return (
    <Drawer.Title className={cn("text-lg font-medium text-neutral-700 ", className)}>
      {children}
    </Drawer.Title>
  );
};

export const DrawerDescription = ({ children }: DrawerDescriptionProps): JSX.Element => {
  return (
    <Drawer.Description className="text-md text-gray-600">{children}</Drawer.Description>
  );
};

export const DrawerClose = ({ children, onClose }: DrawerCloseProps): JSX.Element => {
  return (
    <Drawer.Close asChild>
      <Button
        fullWidth
        className="text-gray-500 bg-transparent w-full hover:bg-gray-100 px-4 py-2 rounded-sm focus:outline-hidden focus:ring-2 focus:ring-gray-300"
        onClick={onClose}
      >
        {children}
      </Button>
    </Drawer.Close>
  );
};

export const DrawerContent = ({
  children,
  className,
  dismissible = true,
  noPadding = false,
}: DrawerContentProps): JSX.Element => {
  return (
    <Drawer.Portal>
      <Drawer.Overlay
        data-testid="drawer-overlay"
        className="fixed z-50 inset-0 bg-black/40"
      />
      <Drawer.Content
        className={cn(
          "z-51 bg-white flex flex-col fixed bottom-base bottom-0 left-0 right-0 max-h-[95vh] rounded-t-[10px]",
          className,
        )}
      >
        {/* Handle do drawer */}
        {dismissible ? (
          <div className="relative flex justify-center pt-4 pb-2">
            <div className="w-12 h-1.5 rounded-full bg-neutral-300" />
          </div>
        ) : null}

        {/* Container principal com flexbox */}
        <div
          className={cn("flex flex-col flex-1 max-w-md w-full mx-auto min-h-0 gap-2", {
            "px-4 pb-4": !noPadding,
            "px-0 pb-0": noPadding,
          })}
        >
          {children}
        </div>
      </Drawer.Content>
    </Drawer.Portal>
  );
};
